/**
 * Whale Cove Off Grid — Cetacean silhouettes.
 *
 * Silhouettes sourced from PhyloPic — a scientific silhouette database
 * used in peer-reviewed publications. All six whale/porpoise/dolphin
 * profiles are by T. Michael Keesey, released under CC BY-SA 3.0.
 * Proper field-guide accuracy, tiny file sizes (<3KB each).
 *
 * Colored via CSS `filter` so the black source turns deep-forest green.
 * All six behave as `<img>` under the hood — drop anywhere and size freely.
 *
 * Attribution must be displayed per CC BY-SA — see LICENSE_NOTE below.
 */
import humpback from "@/assets/silhouettes/humpback.svg";
import fin from "@/assets/silhouettes/fin.svg";
import minke from "@/assets/silhouettes/minke.svg";
import right from "@/assets/silhouettes/right.svg";
import porpoise from "@/assets/silhouettes/porpoise.svg";
import dolphin from "@/assets/silhouettes/dolphin.svg";

// CSS filter recipe to recolor a black SVG to the wc-forest palette (#2A3A2A).
// Computed with https://codepen.io/sosuke/pen/Pjoqqp
const FOREST_FILTER =
  "brightness(0) saturate(100%) invert(20%) sepia(9%) saturate(1099%) hue-rotate(63deg) brightness(95%) contrast(85%)";

function SilhouetteImg({ src, alt, ...props }) {
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      style={{ filter: FOREST_FILTER, maxWidth: "100%", height: "auto" }}
      {...props}
    />
  );
}

export const HumpbackSilhouette = (p) => <SilhouetteImg src={humpback} alt="Humpback whale silhouette" {...p} />;
export const FinWhaleSilhouette = (p) => <SilhouetteImg src={fin} alt="Fin whale silhouette" {...p} />;
export const MinkeWhaleSilhouette = (p) => <SilhouetteImg src={minke} alt="Minke whale silhouette" {...p} />;
export const RightWhaleSilhouette = (p) => <SilhouetteImg src={right} alt="North Atlantic right whale silhouette" {...p} />;
export const HarborPorpoiseSilhouette = (p) => <SilhouetteImg src={porpoise} alt="Harbor porpoise silhouette" {...p} />;
export const WhiteSidedDolphinSilhouette = (p) => <SilhouetteImg src={dolphin} alt="Atlantic white-sided dolphin silhouette" {...p} />;

export const SILHOUETTE_MAP = {
  "Humpback Whale": HumpbackSilhouette,
  "Fin Whale": FinWhaleSilhouette,
  "Minke Whale": MinkeWhaleSilhouette,
  "North Atlantic Right Whale": RightWhaleSilhouette,
  "Harbor Porpoise": HarborPorpoiseSilhouette,
  "Atlantic White-sided Dolphin": WhiteSidedDolphinSilhouette,
};

// Required attribution for CC BY-SA 3.0 usage.
export const LICENSE_NOTE = {
  author: "T. Michael Keesey",
  source: "PhyloPic",
  sourceUrl: "https://www.phylopic.org",
  license: "CC BY-SA 3.0",
  licenseUrl: "https://creativecommons.org/licenses/by-sa/3.0/",
};
